import { Link } from "react-router-dom";
import { Mail, MapPin, ArrowUpRight, Lock } from "lucide-react";

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#0B192C] text-white border-t-2 border-[#C5A059]">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src="/assets/ncs-mark.png?v=2" alt="NCS logo mark" className="w-11 h-11 object-contain" />
            <span className="leading-none">
              <span className="block font-display font-semibold text-2xl tracking-tight">NCS</span>
              <span className="block font-mono-brand text-[9px] uppercase tracking-[0.22em] text-white/50 mt-1">
                National Clinical Services Ltd
              </span>
            </span>
          </div>
          <p className="mt-6 text-sm leading-relaxed text-white/60 max-w-sm">
            Supporting Primary Care. Delivering Clinical Excellence. Clinical pharmacy services and
            workforce solutions for PCNs, GP practices and NHS organisations across the UK.
          </p>
          <div className="mt-6 flex gap-2">
            {["#0F382C", "#0D9488", "#C5A059", "#7A1F2B"].map((c) => (
              <span key={c} className="w-6 h-px" style={{ backgroundColor: c }} />
            ))}
          </div>
          <p className="mt-5 font-display text-sm italic text-white/40">Care, delivered properly.</p>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Ways to work with us</h4>
          <ul className="mt-5 space-y-3 text-sm divide-y divide-white/5">
            <li className="pt-3 first:pt-0"><Link data-testid="footer-link-commission" to="/commission-clinical-services" className="text-white/70 hover:text-white transition-colors">Commission Clinical Services</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-workforce" to="/request-clinical-workforce" className="text-white/70 hover:text-white transition-colors">Request Clinical Workforce</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-transport" to="/transport" className="text-white/70 hover:text-white transition-colors">Transport Services</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-arrs" to="/arrs-nhs-support" className="text-white/70 hover:text-white transition-colors">ARRS, PCN DES & NHS Support</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-international" to="/international" className="text-white/70 hover:text-white transition-colors">NCS International — Middle East</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-ooh" to="/out-of-hours" className="text-white/70 hover:text-white transition-colors">NCS Out of Hours</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-community" to="/community-pharmacy" className="text-white/70 hover:text-white transition-colors">NCS Community Pharmacy</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-clinic" to="/ncs-clinic" className="text-white/70 hover:text-white transition-colors">NCS Clinic</Link></li>
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Company</h4>
          <ul className="mt-5 space-y-3 text-sm divide-y divide-white/5">
            <li className="pt-3 first:pt-0"><Link data-testid="footer-link-about" to="/about" className="text-white/70 hover:text-white transition-colors">About NCS</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-case-study" to="/case-study" className="text-white/70 hover:text-white transition-colors">Case study</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-insights" to="/insights" className="text-white/70 hover:text-white transition-colors">Insights</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-careers" to="/careers" className="text-white/70 hover:text-white transition-colors">Join Our Pharmacy Team</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-contact" to="/contact" className="text-white/70 hover:text-white transition-colors">Contact</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-privacy" to="/privacy-policy" className="text-white/70 hover:text-white transition-colors">Privacy Policy</Link></li>
            <li className="pt-3"><Link data-testid="footer-link-admin" to="/admin" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white transition-colors"><Lock size={12} /> Staff login</Link></li>
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-[#C5A059]">Contact</h4>
          <ul className="mt-5 space-y-3 text-sm text-white/70">
            <li className="flex items-center gap-2.5"><Mail size={14} className="text-[#C5A059]" /> <a href="mailto:yasin@nationalclinicalservices.co.uk" className="hover:text-white transition-colors">yasin@nationalclinicalservices.co.uk</a></li>
            <li className="flex items-start gap-2.5"><MapPin size={14} className="text-[#C5A059] mt-0.5" /> Supporting PCNs, GP practices and NHS organisations UK-wide</li>
          </ul>
          <Link
            to="/contact"
            data-testid="footer-cta-link"
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white border border-white/20 rounded-full px-5 py-2.5 hover:bg-white hover:text-[#0B192C] transition-colors duration-300"
          >
            Make an enquiry <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>

      <div aria-hidden="true" className="mt-16 mb-10 select-none pointer-events-none overflow-hidden border-t border-white/10 pt-10">
        <p className="font-display font-semibold text-[20vw] lg:text-[230px] leading-none text-center text-outline tracking-tight">
          NCS
        </p>
      </div>

      <div className="relative mt-0 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
        <p className="text-xs text-white/40" data-testid="footer-copyright">
          © {new Date().getFullYear()} National Clinical Services Ltd. All rights reserved.
        </p>
        <p className="text-xs text-white/40 text-center sm:text-right" data-testid="footer-compliance">
          All services are subject to formal quotation, clinical governance, availability and contract.
        </p>
      </div>
    </div>
  </footer>
);

export default Footer;
